import { createContext, useContext } from "react";

export interface AdminUser {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  photoUrl?: string;
}

export interface AdminNotification {
  id: number;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  sentAt: string;
  linkUrl?: string;
}

export type LivestreamRealtimeType =
  | "livestream.updated"
  | "livestream.viewers.changed"
  | "livestream.analytics.tick"
  | "livestream.interaction.changed"
  | "livestream.media.runtime"
  | "livestream.breakout.changed"
  | "livestream.obs.diagnostics"
  | "livestream.call.changed"
  | "livestream.source.changed"
  | "livestream.relay.status";

export interface LivestreamRealtimeMessage {
  type: LivestreamRealtimeType;
  livestreamId: number;
  payload: Record<string, unknown>;
  emittedAt: string;
}

export interface SupportCallIncomingMessage {
  type: "support.call.incoming";
  roomId: string;
  chatRoomId: number;
  ticketId: number | null;
  callerUserId: number;
  callerName: string | null;
  at: string;
}

export type WebSocketMessage =
  | { type: "ping" }
  | { type: "online_admins"; admins: AdminUser[] }
  | { type: "admin_online"; admin: AdminUser }
  | { type: "admin_offline"; adminId: number | string }
  | { type: "notification"; data: AdminNotification }
  | { type: "unread_count"; count: number }
  | SupportCallIncomingMessage
  | LivestreamRealtimeMessage;

export interface WebSocketContextType {
  socket: WebSocket | null;
  isConnected: boolean;
  lastMessage: WebSocketMessage | null;
  onlineAdmins: AdminUser[];
  send: (type: string, data?: Record<string, unknown>) => void;
}

export const WebSocketContext = createContext<WebSocketContextType | undefined>(
  undefined,
);

export const useWebSocket = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
};
